import { Chip } from "@evidenceb/athena-common/design-system/Chip";
import type { ThemeType } from "@evidenceb/athena-common/modules/Theme";
import type { Statement } from "@xapi/xapi";
import { defineMessages, type IntlShape } from "react-intl";
import {
  type ExperimentationConfig,
  type ExperimentationModeConfig,
  type ExperimentationPool,
  ExperimentationTestStatus,
} from "../../../interfaces/Config";
import type { Group } from "../../../interfaces/Session";
import { UserType } from "../../../interfaces/User";
import { getExerciseId } from "../../../utils/statements/statement-parse";
import { byEntryLearningSetIdFilter } from "../../../utils/statements/statements";
import { latestByItemId } from "./Student/utils";
import type {
  ClassroomsProgress,
  Experimentation,
  ExperimentationSet,
  Test,
} from "./type";

export const isDisabled = (
  status: ExperimentationTestStatus | undefined,
  isFinished?: boolean,
) => {
  return (
    isFinished ||
    status === ExperimentationTestStatus.INCOMING ||
    isClosed(status)
  );
};

/** Students cannot go back to a phase they completed. */
export const isClosed = (status: ExperimentationTestStatus | undefined) =>
  status === ExperimentationTestStatus.CLOSED ||
  status === ExperimentationTestStatus.FINISHED;

export const getTestStatus = (
  testId: string,
  tests: ExperimentationConfig | undefined,
) => {
  if (flattenPool(tests?.pre)?.includes(testId)) return tests?.preStatus;
  if (flattenPool(tests?.post)?.includes(testId)) return tests?.postStatus;
};

/**
 * A teacher can review a test they fully answered themselves, until the
 * phase is closed.
 * Pool-mates of a finished test count as finished but have no answers.
 */
export const isReviewable = (
  status: ExperimentationTestStatus | undefined,
  results: Statement[] | undefined,
  itemIds: string[],
) =>
  (status === ExperimentationTestStatus.ENABLED ||
    status === ExperimentationTestStatus.FINISHED) &&
  itemIds.length > 0 &&
  itemIds.every((itemId) =>
    results?.some((statement) => getExerciseId(statement) === itemId),
  );

export const getDisabledDescription = (
  status: ExperimentationTestStatus | undefined,
  color: string,
  theme: ThemeType,
  intl: IntlShape,
) => {
  if (!status) return;
  if (status === ExperimentationTestStatus.INCOMING)
    return (
      <Chip
        icons={[{ path: "access_alarms", color: color }]}
        input={intl.formatMessage(experimentationMessages.incoming)}
        colorMode={theme}
      />
    );
  if (isClosed(status))
    return (
      <Chip
        icons={[{ path: "padlock", color: color }]}
        input={intl.formatMessage(experimentationMessages.closed)}
        colorMode={theme}
      />
    );
};

export const isTestEnabled = (
  testId: string,
  tests: ExperimentationConfig | undefined,
) => {
  if (
    flattenPool(tests?.pre)?.includes(testId) &&
    tests?.preStatus === ExperimentationTestStatus.ENABLED
  ) {
    return true;
  }
  if (
    flattenPool(tests?.post)?.includes(testId) &&
    tests?.postStatus === ExperimentationTestStatus.ENABLED
  ) {
    return true;
  }
  return false;
};

export const parseTests = (
  experimentationSets: ExperimentationSet<Test>[] | undefined,
  statements: Statement[] | undefined,
  preTestsIds: string[] | undefined,
  pools: ExperimentationPool,
): Experimentation[] | undefined => {
  if (!experimentationSets || experimentationSets.length === 0 || !preTestsIds)
    return;
  const closedPools: string[] = [];
  const rawExperimentations = experimentationSets.map((set) => {
    const results = getTestResults(set.learning_set_id, statements);
    const currentPool = Object.entries(pools).find(([_key, value]) => {
      return value.includes(set.learning_set_id);
    });

    const currentPoolName = currentPool![0];
    const isFinished = results?.length === set.learning_items.length;
    if (isFinished) {
      closedPools.push(currentPoolName);
    }

    return {
      data: set,
      results: results,
      averageProgress: undefined,
      classroomsProgress: undefined,
      isPretest: preTestsIds.includes(set.learning_set_id),
      isFinished: isFinished,
      poolName: currentPoolName,
    };
  });
  rawExperimentations.forEach((expe) => {
    if (closedPools.includes(expe.poolName)) expe.isFinished = true;
  });
  return rawExperimentations;
};

export const experimentationConfigKey = (userType: UserType) =>
  userType === UserType.Teacher ? "teacherTest" : "studentTest";

export const getExperimentationConfig = (
  test: ExperimentationModeConfig | undefined,
  userType: UserType,
): ExperimentationConfig | undefined =>
  test?.[experimentationConfigKey(userType)];

export const isBlocking = (
  test: ExperimentationConfig | undefined,
  userType: UserType,
) => test?.blocking ?? userType === UserType.Student;

/**
 * The config is merged from several fragments, which may still declare the
 * student tests directly under `mode.test` (e.g. the phase fragments only set
 * the statuses and step). Move these keys under `mode.test.studentTest`.
 *
 * The teacher tests follow the same phase (statuses and step) as the student
 * tests unless they set their own.
 */
export const normalizeExperimentationConfig = (
  test:
    | (Partial<ExperimentationConfig> & {
        studentTest?: Partial<ExperimentationConfig>;
        teacherTest?: Partial<ExperimentationConfig>;
      })
    | undefined,
): ExperimentationModeConfig | undefined => {
  if (!test) return;
  const { studentTest, teacherTest, ...flatStudentTest } = test;
  const normalizedStudentTest = (
    Object.keys(flatStudentTest).length > 0
      ? { ...flatStudentTest, ...studentTest }
      : studentTest
  ) as ExperimentationConfig | undefined;
  const normalizedTeacherTest =
    teacherTest &&
    ({
      ...(normalizedStudentTest && {
        preStatus: normalizedStudentTest.preStatus,
        postStatus: normalizedStudentTest.postStatus,
        step: normalizedStudentTest.step,
      }),
      ...teacherTest,
    } as ExperimentationConfig);
  return {
    ...(normalizedStudentTest && { studentTest: normalizedStudentTest }),
    ...(normalizedTeacherTest && { teacherTest: normalizedTeacherTest }),
  };
};

/**
 * The insight API only returns the students who started the test: add the
 * students of the teacher's classrooms that did not, with no exercise done.
 */
export const mergeWithClassrooms = (
  classroomsProgress: ClassroomsProgress[] | undefined,
  classrooms: Group[] | undefined,
): ClassroomsProgress[] => {
  const progress = classroomsProgress ?? [];
  if (!classrooms) return progress;
  const merged = classrooms.map((classroom) => {
    const progressClassroom = progress.find((p) => p.id === classroom.id);
    const studentsProgress = classroom.students.map(
      (student) =>
        progressClassroom?.students_progress.find(
          (p) => p.id === student.id,
        ) ?? {
          id: student.id,
          first_name: student.firstname,
          last_name: student.lastname,
          exercises_done: 0,
        },
    );
    // Keep students returned by the API but missing from the session
    const extraStudents =
      progressClassroom?.students_progress.filter(
        (p) => !classroom.students.some((student) => student.id === p.id),
      ) ?? [];
    return {
      id: classroom.id,
      name: classroom.name,
      students_progress: [...studentsProgress, ...extraStudents],
    };
  });
  const extraClassrooms = progress.filter(
    (p) => !classrooms.some((classroom) => classroom.id === p.id),
  );
  return [...merged, ...extraClassrooms].filter(
    (classroom) => classroom.students_progress.length > 0,
  );
};

/**
 * Students finish a pool once they finished any of its tests.
 */
export const areStudentsPreTestsFinished = (
  tests: Experimentation[],
  pre: ExperimentationPool | undefined,
) =>
  Object.values(pre ?? {}).every((poolTestIds) => {
    const poolTests = tests.filter((test) =>
      poolTestIds.includes(test.data.learning_set_id),
    );
    const progresses = poolTests.flatMap((test) =>
      (test.classroomsProgress ?? []).flatMap((classroom) =>
        classroom.students_progress.map((progress) => ({
          studentId: progress.id,
          isFinished:
            progress.exercises_done >= test.data.learning_items.length,
        })),
      ),
    );
    return progresses.every(({ studentId }) =>
      progresses.some(
        (progress) => progress.studentId === studentId && progress.isFinished,
      ),
    );
  });

export const flattenPool = (pool: ExperimentationPool | undefined) => {
  if (!pool) return;
  return Object.values(pool).flat();
};

export const getTestResults = (
  id: string,
  statements: Statement[] | undefined,
) => {
  if (statements && id) {
    return latestByItemId(statements.filter(byEntryLearningSetIdFilter(id)));
  }
};

export const experimentationMessages = defineMessages({
  experimentationListError: {
    id: "experimentation-list-students-loading-error",
    defaultMessage: "An error occurred when loading the tests.",
  },
  preTests: {
    id: "experimentation-preTests",
    defaultMessage: "Pre Tests",
  },
  postTests: {
    id: "experimentation-postTests",
    defaultMessage: "Post Tests",
  },
  incoming: {
    id: "experimentation-incomingMsg",
    defaultMessage: "Available soon",
  },
  closed: {
    id: "experimentation-closedMsg",
    defaultMessage: "Closed",
  },
});
